import type { GameAttributesLeague } from "../../../../common/types.ts";
import i18n from "./i18n.ts";

export const getCurrencyFormat = (
	language = i18n.language,
): GameAttributesLeague["currencyFormat"] =>
	language.startsWith("zh") ? ["", ".", "美元"] : ["$", ".", ""];

// R6-1: Chinese money habit uses 万 (10^4) / 亿 (10^8) rather than the English
// k/M/B ladder, so amounts read as "2970万美元" / "2.97亿美元" instead of the
// confusing "29.7M美元" that formatCurrencyBase produced from the zh suffix. This
// returns null when the active language is not Chinese, so callers keep falling
// back to the shared English-style formatCurrencyBase (en presentation unchanged).
export const formatCurrencyZh = (
	amount: number,
	initialUnits: "M" | "" = "",
	language = i18n.language,
): string | null => {
	if (!language.startsWith("zh")) {
		return null;
	}

	// formatCurrency stores money in millions when initialUnits === "M"; a few
	// call sites (e.g. ticket price) pass raw dollars.
	const dollars = initialUnits === "M" ? amount * 1e6 : amount;
	const sign = dollars < 0 ? "-" : "";
	const abs = Math.abs(dollars);

	const format = (value: number, unit: string) => {
		let numberString = value.toFixed(2);
		// Strip trailing zeros so 2970.00 -> 2970 and 2.50 -> 2.5.
		if (numberString.includes(".")) {
			numberString = numberString.replace(/\.?0+$/, "");
		}
		return `${sign}${numberString}${unit}美元`;
	};

	if (abs >= 1e8) {
		return format(abs / 1e8, "亿");
	}
	if (abs >= 1e4) {
		return format(abs / 1e4, "万");
	}
	// Below 1万 keep whole dollars (ticket prices, near-zero cash) to avoid an
	// awkward "0.5万美元".
	return format(abs, "");
};
